import { DateTime, Interval } from 'luxon';
const titleCaseAndCleanExclusions = ['CTP'];
export function titleCaseClean(value: any, options?: any): any {
  let formattedString = '';
  if (value) {
    if (titleCaseAndCleanExclusions.includes(value)) {
      formattedString = value;
    } else {
      formattedString = titleCase(value);
      formattedString = formattedString.split('_').join(' ');
    }
    if (options?.capitalCase) {
      formattedString = formattedString
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    if (options?.removeWords) {
      formattedString = formattedString
        .split(' ')
        .filter((word) => !options.removeWords.includes(word))
        .join(' ');
    }
    return formattedString;
  } else {
    if (options?.capitalCase) {
      formattedString = formattedString
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return formattedString;
  }
}

export function camelCaseClean(value: any): any {
  if (!value) {
    return '';
  }

  const result = value.replace(/([a-z])([A-Z])/g, '$1 $2');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

export function camelCase(value: any): string {
  if (!value || typeof value !== 'string') {
    return '';
  }
  // Remove leading/trailing whitespace and split by spaces, underscores, hyphens, or camelCase boundaries
  const words = value
    .replace(/([a-z])([A-Z])/g, '$1 $2') // split camelCase
    .replace(/[_-]+/g, ' ') // treat underscores/hyphens as spaces
    .trim()
    .split(/\s+/);

  if (words.length === 0) {
    return '';
  }

  // Lowercase the first word, capitalize the rest, and join without spaces
  const camelCased = [
    words[0].charAt(0).toLowerCase() + words[0].slice(1),
    ...words
      .slice(1)
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ),
  ].join('');

  return camelCased;
}

export function titleCase(str: any) {
  str = str.toLowerCase().split(' ');
  if (str) {
    for (let i = 0; i < str.length; i++) {
      str[i] = str[i].charAt(0).toUpperCase() + str[i].slice(1);
    }
    return str.join(' ');
  }
  return str;
}
export function upperSnakeCaseToCamelCase(value: string): string {
  if (!value) {
    return '';
  }

  const words = value.split('_');
  const camelCaseWords = words.map((word, index) => {
    if (index === 0) {
      return word.toLowerCase();
    } else {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    }
  });

  return camelCaseWords.join('');
}

export function internationalizedDate(
  value: DateTime | string,
  format: string,
  relative?: boolean,
  type?: string,
  locale?: string
) {
  if (!value) {
    return '';
  }
  if (!format) {
    format = 'D';
  }

  switch (format) {
    case 'CONDENSED_DATE':
      format = 'D'; // 2/01/2024
      break;
    case 'DATETIME_SHORT':
      format = 'f'; //8/6/2014, 1:07 PM
      break;
    case 'DATETIME_SHORT_WITH_SECONDS':
      format = 'F'; //DISPATCH LOGS - ABOVE WITH SECONDS
      break;
    case 'LOCALIZED_TIME':
      format = 't'; //01:30 pm
      break;
    case 'SHORT_DATE':
      format = 'ccc d LLL yyyy'; //Fri 26 Nov 2021
      break;
    case 'TIME_OFFSET':
      format = 'hh:mm a ZZZ'; //time offset - 12:17 pm +1100
      break;
    case 'CONDENSED_DATE_TIME_OFFSET':
      format = 'D hh:mm a ZZZ'; //2/01/2024 12:17 pm +1100
      break;
    case 'LOCALISED_DATE_FULL':
      format = 'DDDD';
      break;
    case 'SHORT_DAY':
      format = 'ccc d LLL'; //Sun 4 Aug
      break;
    case 'SHORT_DAY_TIME':
      format = 'ccc d LLL t'; //Sun 4 Aug, 1:30 PM
      break;
    case 'SHORT_DAY_YEAR':
      format = 'ccc d LLL yyyy'; //Sun 4 Aug 2024
      break;
    case 'SHORT_DAY_YEAR_TIME':
      format = 'ccc d LLL yyyy t'; //Sun 4 Aug 2024
      break;
    case 'MONTH_DAY_YEAR':
      format = 'DDD'; //March 23rd 2025
      break;

    default:
  }

  // Ensure value is a DateTime object
  if (typeof value === 'string') {
    value = DateTime.fromISO(value);
  }

  if (type === 'millis') {
    const date = DateTime.fromMillis(+value * 1000);
    return date.toFormat(format);
  }

  if (type === 'durationSince') {
    const now = DateTime.local();

    const interval = Interval.fromDateTimes(value, now);
    const duration = interval.toDuration(['hours', 'minutes', 'days']);

    if (duration.days > 1) {
      return `${duration.days.toFixed(0)} days ago`;
    } else if (duration.days === 1) {
      return `${duration.days.toFixed(0)} day ago`;
    } else if (duration.hours > 1) {
      return `${duration.hours.toFixed(0)} hrs ago`;
    } else if (duration.hours === 1) {
      return `${duration.hours.toFixed(0)} hr ago`;
    } else if (duration.minutes > 1) {
      return `${duration.minutes.toFixed(0)} mins ago`;
    } else {
      return `1 min ago`;
    }
  }

  if (relative) {
    const relativeDate = value.toRelativeCalendar({
      locale: locale,
    });

    // Only return relative date if it's yesterday, today or tomorrow
    if (
      relativeDate &&
      ['yesterday', 'today', 'tomorrow'].includes(relativeDate.toLowerCase())
    ) {
      return relativeDate.charAt(0).toUpperCase() + relativeDate.slice(1);
    }
  }

  return value.setLocale(locale).toFormat(format);
}

export function getReadableFromCamelCase(fieldName: string): string {
  return fieldName
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
